<template>
  <div
    ref="datepickerRef"
    class="hasDatepicker"
    :class="[{ 'inline-mode': inline, 'has-btn': reset,  'positon-right' : !resetPostion}, dateClass]"
  >
    <Datepicker 
      :model-value="modelValue"
      v-bind="getOptions"
      :flow="['calendar']"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template #day="{ day, date }">
        <template v-if="calcDate(date) === today">
          {{ day }}
          <span>오늘</span>
        </template>
        <template v-else>
          {{ day }}
        </template>
      </template>

    </Datepicker>

    <!-- 초기화 (s) -->
    <button
      type="button"
      class="btn-type05 v2 btn-reset"
      @click="selectedDateClear"
      v-if="reset"
    >
      초기화
    </button>
    <!-- //초기화 (e) -->
  </div>
</template>
<script setup lang="ts">
import { ref, watch, computed, defineExpose } from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import type { VueDatePickerProps } from "@vuepic/vue-datepicker";

defineOptions({
  name: "DatepickerComponent",
});

// props
interface Props {
  disabledDates?: Date[] | ((date: Date) => boolean);
  disablePastDates?: boolean;
  disableInRange?: boolean; //비활성 날짜 포함 여부
  disabled?: boolean; //비활성
  range?: boolean;
  inline?: boolean;
  multiCalendar?: boolean;
  autoApply?: boolean;
  placeholder?: string;
  dateInput?: any;
  reset?: boolean; //초기화 버튼 여부
  custom?: string; //커스텀
  resetPostion?:boolean; 
  defaultInput?: any; // 인풋에 기본날짜 지정
  modelValue?: Date[] | [Date, Date] | null | Date
  dateClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabledDates: () => [],
  disablePastDates: false,
  disableInRange: true,
  range: false,
  inline: false,
  multiCalendar: false,
  autoApply: true,
  placeholder: "날짜를 선택하세요",
  reset: false,
  custom: "",
  defaultInput: undefined,
  dateClass: '',
});

const isDateDisabled = computed(() => {
  return (date: Date) => {
    // 오늘 날짜 포함하여 이전 날짜 체크 (disablePastDates가 true일 때만)
    if (props.disablePastDates) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date <= today) return true; // '<' 에서 '<=' 로 변경
    }

    // props로 전달된 disabledDates 체크
    if (typeof props.disabledDates === "function") {
      return props.disabledDates(date);
    } else if (Array.isArray(props.disabledDates)) {
      return props.disabledDates.some((disabledDate) => {
        const disabled = new Date(disabledDate);
        return (
          disabled.getFullYear() === date.getFullYear() &&
          disabled.getMonth() === date.getMonth() &&
          disabled.getDate() === date.getDate()
        );
      });
    }
    return false;
  };
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date | [Date, Date] | null) : void
}>();

type ModelValue = VueDatePickerProps["modelValue"];
const translations = {
  days: ["일", "월", "화", "수", "목", "금", "토"],
  months: [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ],
} as const;


const getOptions = computed(() => {
  return {
    inline: props.inline,
    autoApply: props.autoApply,
    // selectText: "선택완료",
    placeholder: props.placeholder,
    disabled: props.disabled,
    translations: translations,
    disabledDates: isDateDisabled.value,
    clearable: false,
    // weekStart: 0,
    offset: 8,
    mode: "date",
    format: "yyyy-MM-dd",
    locale: "ko",
    enableTimePicker: false,
    calendarClass: "custom-calendar-icon",
    enableMonthYearSelect: true,
    multiCalendars: props.range ? 2 : props.multiCalendar,
    range: props.range,
    multiCalendarsSolo: true,
    textInput: {
      rangeSeparator: " ~ ",
    },

  }
});

const datepickerRef = ref<HTMLElement | null>(null);
const selectedDate = ref<ModelValue>(null);

// 20250930 인풋에 기본 값 출력
onMounted(() => {
  if (props.defaultInput && props.range) {
    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);

    start.setDate(today.getDate() + props.defaultInput[0]);
    end.setDate(today.getDate() + props.defaultInput[1]);

    selectedDate.value = [start, end];
  }
});

const selectedDateClear = () => {
  selectedDate.value = "";
};

const today = ref(
  `${new Date().getFullYear()}${new Date().getMonth()}${new Date().getDate()}`
);

const calcDate = (date: Date) => {
  return `${date.getFullYear()}${date.getMonth()}${date.getDate()}`;
};

watch(() => props.modelValue, (newVal) => {
  selectedDate.value = newVal as ModelValue;
},
{ immediate: true, deep: true }
)

defineExpose({ selectedDateClear });
</script>

<style lang="scss" scoped>
@use "@/assets/scss/pc/common/variables-hcon" as v;

.hasDatepicker{ 
  width: 100%;
  height: 40px;

	&.md {
		height: 48px;
	}

	&.lg {
		height: 56px;
	}

  :deep(.dp__main) {
    width: 100%;
		height: 100%;
    .dp__month_year_wrap {
      display: flex;
      flex-direction: row; // 기본값
      justify-content: center;
      align-items: center;
      gap: 0 16px;
      font-size: 14px;
      // font-weight: 500; // 볼드체 제거
      color: #1e1e1e;
      // pointer-events: none; // 클릭 비활성화
      .dp--arrow-btn-nav {
        display: block;
        width: 12px;
        height: 12px;
        background: url("@/assets/images/pc/icon/ico_arrow_welfare_swiper_left.svg")
          no-repeat center center;
        background-size: 6px auto;
        border: none;
        &[data-dp-element="action-next"] {
          transform: rotate(180deg);
        }
        .dp__inner_nav {
          display: none;
        }
      }
      .dp__month_year_select {
        position: relative;
        width: initial;
        padding-right: 12px;
        &[data-dp-element="overlay-month"] {
          order: 2;
        }
        &[data-dp-element="overlay-year"] {
          order: 1;
          &::after {
            content: "년";
          }
        }
        &::before {
          content: "";
          position: absolute;
          top: 50%;
          right: 0;
          width: 8px;
          height: 6px;
          margin-left: 8px;
          background-image: url("@/assets/images/pc/icon/ico_arrow_bottom2.svg");
          background-position: right center;
          background-repeat: no-repeat;
          background-size: 8px auto;
          transform: translateY(-50%);
        }
      }
    }

    > div {
			height: 100%;
		}
  }
	:deep(.custom-month-year) {
    font-size: 14px;
    font-weight: 500;
    color: #1e1e1e;
    pointer-events: none;  // 클릭 비활성화
  }
  :deep(.dp__input) { 
    width: 100%;
		height: 100%;
		padding: 0 42px 0 12px;
		border: 1px solid #999999;
		border-radius: 8px;
		font-size: 14px;
		font-weight: 400;
		line-height: 40px;
		color: v.$font-main-color;

		&:read-only,
		&:disabled {
			background-color: #dfdfdf;
			border: 1px solid #cccccc;
			color: v.$font-tertiary-color;
      cursor: not-allowed;
      
      &:focus,
      &:hover {
        border: 1px solid #cccccc;
      }
		}
		&::-webkit-input-placeholder {
			color: v.$font-tertiary-color;
		}
		&::placeholder {
			color: v.$font-tertiary-color;
		}
		&:hover,
		&:focus {
			border-color: #0a583f;
		}
	}
	.dp__input_wrap {
		height: 100%;
	}
	:deep(.dp__input_wrap) {
		height: 100%;
		&:after {
			content: '';
			display: block;
			position: absolute;
			right: 16px;
			transform: translateY(-50%);
			top: 50%;
			background-image: url('@/assets/images/pc/icon-hcon/icon-calendar.svg');
			background-position: center;
			background-repeat: no-repeat;
			background-size: contain;
			width: 20px;
			height: 20px;
		}

    &:has(input:disabled)::after {
      background-image: url('@/assets/images/pc/icon-hcon/icon-calendar-gray.svg');
    }
	}

  // multicalendar의 경우, 달력 사이의 간격 추가
  :deep(.dp__instance_calendar + .dp__instance_calendar) {
    margin-left: 24px;
  }

	:deep(.dp__input_icon) {
		display: none;
	}
  :deep(.dp--menu-wrapper) {
		left: 0 !important; // 강제로 왼쪽 정렬
		transform: none !important; // 중앙 정렬 transform 제거
		right: auto;
		.dp__menu {
    	width: 100%;
			border-radius: 8px;
			.dp__arrow_top {
				display: none;
			}
			.dp--header-wrap {
				margin-bottom: 24px;
				.month-year-wrapper {
					display: flex;
					width: 100%;
					align-items: center;
					justify-content: space-between;
					padding: 0 7px;
					.custom-month-year {
						font-weight: 400;
					}
					.dp__month_year_select {
						display: block;
						width: 17px;
						height: 17px;
						background: url('@/assets/images/pc/icon-hcon/ico_arrow_r5.svg') no-repeat center center;
						background-size: 8px auto;
						border: none;
						.hidden {
							font-size: 0;
							line-height: 0;
							opacity: 0;
							color: transparent;
						}
						&:first-child {
							transform: rotate(180deg);
						}
					}
				}
			}
			.dp__calendar {
				.dp__calendar_header_separator {
					display: none;
				}
				.dp__calendar_header {
					justify-content: center;
					align-items: center;
					gap: 10px;
					.dp__calendar_header_item {
						padding: 0;
						width: 32px;
						height: 32px;
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 13px;
						color: #666666;
						font-weight: 400;
					}
				}
				.dp__calendar_row {
					margin: 10px 0 0;
					gap: 10px;
				}
			}
		}
	}
	:deep(.dp__menu_inner) {
		padding: 32px 32px 24px;
	}
	:deep(.dp__theme_light) {
    --dp-menu-border-color: #1e1e1e;
		--dp-hover-color: #dddddd;
  }
	:deep(.dp__cell_inner) {
    flex-direction: column;
		--dp-cell-size: 32px;
		border-radius: 6px;
		font-size: 11px;
		line-height: 16px;
		color: #1e1e1e;

    &.dp__active_date,
    &.dp__range_end,
    &.dp__range_start {
      background: #0a583f;
      color: #fff;
      font-weight: 700;
    }

		&.dp__active_date{
			background: #0a583f;
			color: #fff;
			font-weight: 700;
		}
		&.dp__cell_disabled {
			color: #cccccc;
		}
	}
	:deep(.dp__today) {
		position: relative;
		flex-direction: column;
		border: none;
    background: #0a583f;
		color: #fff !important;
		// &:after {
		// 	content: '오늘';
		// 	font-size: 11px;
		// 	line-height: 13px;
		// }
	}

  &.positon-right {
    :deep(.dp--menu-wrapper) {
      left: auto !important;
      right: 0;
    }
  }

  :deep(.dp__overlay) {
    border-radius: 7px;
  }

  :deep(.dp__container_block) {
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>